import styled from 'styled-components'
import { SubHeader } from '../GvSubHeader'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getSeriesFrame,
  getSessionInitDataState,
  pushPageViewTrackingEvent,
  setMarketDenomination,
  setSeriesFrame,
  setShowFullPageViewer,
  setUnavailableImageFrames,
  setUserSelectionTimestamp,
} from '../../../store/sessionDataSlice'
import { AllViewFooter } from './AllViewFooter'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { PortraitFooter } from '../../../components/Footer/PortraitFooter'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { CustomScrollbarMixin } from '../../../css/CustomScrollbarMixin'
import { TdsButton, TdsIcon, TdsSpinner, TdsTooltip } from '@scania/tegel-react'
import { useClient } from '../../../utils/useClient'
import {
  getMenuState,
  setCurrentlyLoadingId,
  setMenuIdToOpenAtJumpToAllView,
  setMenuTreeAllView,
} from '../../../store/menuSlice'
import {
  ConfigChangeMenuEntry,
  ConfigChangeResult,
  GuidedViewMenuLeafNode,
  GuidedViewMenuNode,
} from '../../../api/generated'
import { SideMenu } from './SideMenuDesktop/SideMenu'
import { BlendColorRgb, BlendColors } from '../BlendColors'
import { getMarketLanguageState } from '../../../store/appSlice'
import {
  SvgArrowRight,
  SvgChevronDown,
  SvgChevronLeft,
  SvgChevronRight,
  SvgInfo,
  SvgMaximize,
  SvgScaniaSuper,
  SvgTick,
} from '../../../components/SvgImports'
import { MainImageArea } from '../MainImage/MainImageArea'
import {
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingAllViewShowTruckButtonClick,
  ScaniaAdobeTrackingClickBmReadMoreEvent,
  ScaniaAdobeTrackingClickFullImageEvent,
  ScaniaAdobeTrackingConfigChangedEvent,
  ScaniaAdobeTrackingMenuClickEvent,
  ScaniaAdobeTrackingPageName,
  pushAdobeEvent,
} from '../../../utils/adobeAnalytics'
import { SMART_DASH_FAMILY_ID } from '../../../api/constants'
import {
  CocOpenedFromType,
  GuidedViewPageType,
  ImageSeriesFrame,
  SidePanelContent,
} from '../../../store/types'
import { SESSION_FAILURE } from '../../../api/errors'
import {
  getLatestConfigChangeResult,
  getTruckInfoState,
  setLatestConfigChangeResult,
} from '../../../store/truckInfoSlice'
import {
  ConsequenceOfChangeDialogData,
  ConsequenceOfChangeHandler,
} from '../../../types/ConsequenceOfChangeTypes'
import { closeAllModals } from '../../../store/modalSlice'
import {
  openGvPanel,
  setGvSidePanelContent,
  setReadMoreNode,
} from '../../../store/sidePanelSlice'
import { ConfiguratorMenuMobile } from './MenuMobile/ConfiguratorMenuMobile'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { useResizeObserver } from '../../../utils/useResizeObserver'
import { Size2d } from '../../../types/Size2d'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import { blinkKeyframesBorder } from '../MenuBlinkAnimationBorder'
import { blinkKeyframesColor } from '../MenuBlinkAnimationColor'
import {
  getUrlCidBevSemiliveParameters,
  UrlParamMode,
} from '../../../utils/getUrlCidParameter'
import { useRfqEysButtonState } from '../RfqEysButtonState'
import { useDrag } from '@use-gesture/react'
import { usePageUpdate } from '../../../utils/useBMPageUpdate'
import { MainImageControlPanelMobile } from '../MainImage/MainImageControlPanelMobile'
import { TestElementTypeId } from '../../../types/TestAttributeId'

interface AllViewRootProps {
  $pageIsReady: boolean
}

const AllViewRoot = styled.main<AllViewRootProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  visibility: ${(props) => (props.$pageIsReady ? '' : 'hidden')};

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    overflow-y: auto;
    overflow-x: hidden;
    ${CustomScrollbarMixin}
  }
`

const AllViewDesktop = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const AllViewMobile = styled.main`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const MainArea = styled.div`
  display: flex;
  flex: 1;
  padding-left: 60px;
  padding-right: 40px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding-left: 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    flex-direction: column;
    flex: none;
    padding-left: 0px;
    padding-right: 0px;
    height: auto;
  }
`

const MobileUpperSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--header-height) - 90px);
  min-height: calc(100vh - var(--header-height) - 90px);
`

const HeaderSection = styled.section`
  margin-bottom: 48px;
  padding-left: 60px;
  padding-right: 120px;
  margin-top: 28px;

  @media screen and (max-width: ${BreakpointWidthPx.XLarge}px) {
    margin-top: 0px;
    margin-bottom: 16px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding-right: 16px;
    padding-left: 32px;
    margin-bottom: 8px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 16px;
    margin-bottom: 8px;
    margin-top: 0px;
  }
`

const HeaderWrapper = styled.div`
  display: flex;
`

const Header = styled.h1`
  max-width: 90%;
  word-break: break-word;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    font-size: 28px;
    font-size: min(3rem, 8vw, 5vh);
    word-break: keep-all;
  }
`

interface ChevronProps {
  $hideText: boolean
}

const ChevronWrapper = styled.div<ChevronProps>`
  height: 24px;
  width: 24px;
  margin-left: 16px;

  transform: ${(props) =>
    props.$hideText ? 'rotate(0deg)' : 'rotate(-180deg)'};
  transition-duration: 150ms;
  transition-property: transform;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  svg {
    width: 24px;
    height: 24px;
  }
`

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--tds-grey-300);
  max-width: 80%;
`

const PageDescription = styled.div`
  font-size: 14px;
  padding-top: 8px;
  padding-bottom: 4px;
`

const UpperMenuWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding-left: 60px;
  padding-right: 120px;
  height: 68px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    height: 48px;
    padding-left: 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding-left: 16px;
    padding-right: 16px;
    justify-content: unset;
  }
`

const LeafHeader = styled.div`
  padding: 32px 16px 32px 0px;

  ::first-letter {
    text-transform: capitalize;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding: 16px 8px 16px 0px;
    font-size: 13px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const LeafSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 16px;
  margin-left: 4px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    gap: 8px;
  }
`

const ColourItem = styled.div`
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

interface ColourBoxProps {
  $isSelected: boolean
  $isHighlighted: boolean
  $color: string
  $isMetallic: boolean
}

const ColourCircle = styled.div<ColourBoxProps>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  outline: ${(props) => (props.$isSelected ? '2px solid black' : 'none')};
  outline-offset: 2px;

  background: ${(props) =>
    !props.$isMetallic
      ? props.$color
      : `linear-gradient(to bottom, ${props.$color} 55%, #ffffff)`};

  animation-duration: 700ms;
  animation-iteration-count: 4;
  animation-name: ${(props) =>
    props.$isHighlighted ? blinkKeyframesColor : 'unset'};

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    width: 60px;
    height: 60px;
  }
`

const TickWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: white;

  svg {
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const SpinnerWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const ColourText = styled.div`
  margin-top: 6px;
  width: 100px;
  overflow: hidden;
  font-size: 12px;
`

interface ShowTruck {
  $showTruck: boolean
}

const LeafContentRoot = styled.section<ShowTruck>`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-left: 24px;
  padding-right: 24px;

  @media screen and (max-width: ${BreakpointWidthPx.Medium}px) and (min-width: ${BreakpointWidthPx.Small}px) {
    display: ${(props) => (props.$showTruck ? 'none' : 'flex')};
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
    height: unset;
    margin-top: 0px;
    padding-right: 0px;
  }
`

const LeafContentScrollWrapper = styled.div`
  ${CustomScrollbarMixin}
  width: 100%;
  display: flex;
  flex: 1 1 0;
  flex-flow: column;
  overflow-y: auto;
  overflow-x: hidden;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    flex: unset;
    overflow: auto;
  }
`

interface CardProps {
  $isSelected: boolean
  $isHighlighted: boolean
}

const OptionCard = styled.div<CardProps>`
  width: 340px;
  height: 192px;
  border-radius: 4px;
  border: ${(props) => (props.$isSelected ? '1.5px' : '1px')} solid
    ${(props) =>
      props.$isSelected ? 'var(--tds-information)' : 'var(--tds-grey-300)'};
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  padding-bottom: 8px;
  padding-left: 16px;
  padding-right: 16px;
  margin-bottom: 8px;

  animation-duration: 700ms;
  animation-iteration-count: 4;
  animation-name: ${(props) =>
    props.$isHighlighted ? blinkKeyframesBorder : 'unset'};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    width: 280px;
    height: 140px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Medium}px) {
    width: 100%;
    margin-right: 16px;
  }
`

const OptionCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  height: 24px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    height: 16px;
  }
`

const OptionCardHeaderText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 16px;

  ::first-letter {
    text-transform: capitalize;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 12px;
  }
`

const OptionCardStatusWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;

  svg {
    height: 24px;
    color: var(--tds-information);
  }
`

const OptionCardContent = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  margin-bottom: 8px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  height: 100%;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    margin-top: 8px;
    margin-bottom: 4px;
  }
`

const CardImage = styled.div`
  width: 100px;
  display: flex;
  min-width: 100px;
  height: 100%;
  margin-left: 8px;
  align-items: flex-start;
  justify-content: flex-end;

  img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
`

const CardTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const CardText = styled.div`
  font-size: 11px;

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 7;
  -webkit-box-orient: vertical;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    -webkit-line-clamp: 4;
  }
`

const ReadMoreAndSuperWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  //padding-top: 6px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px) {
    //padding-top: 3px;
  }
`

const CardSuper = styled.div`
  background-color: var(--tds-blue-500);
  border-radius: 16px;
  width: fit-content;
  padding: 2px 12px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    padding: 1px 10px;
  }

  svg {
    height: 12px;

    @media screen and (max-width: ${BreakpointWidthPx.Large}px),
      screen and (max-height: ${BreakpointHeightPx.Large}px) {
      height: 9px;
    }

    path {
      fill: white;
    }
  }
`

const OptionCardReadMore = styled.div`
  display: flex;
  gap: 8px;

  svg {
    height: 14px;
  }
`

const OptionCardReadMoreText = styled.div`
  text-decoration: underline;
  font-size: 12px;
`

interface ImageProps {
  $showFullImage: boolean
  $imageWidth: number
}

const ImageSection = styled.div<ImageProps>`
  width: ${(props) =>
    props.$showFullImage
      ? `${props.$imageWidth}px`
      : `${props.$imageWidth * 0.3}px`};
  display: flex;
  align-items: center;

  transition-duration: 400ms;
  transition-property: width;
`

const SlideInImageButton = styled.div`
  min-width: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const DotAndArrowWrapper = styled.div``

interface GreyDotProps {
  $showFullImage: boolean
}

const GreyDot = styled.div<GreyDotProps>`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: var(--tds-grey-300);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    transform: ${(props) =>
      props.$showFullImage ? 'rotate(0deg)' : 'rotate(180deg)'};
  }
`

const TextSlideIn = styled.div`
  font-size: 12px;
  margin-top: 8px;
`

interface ImageWrapperProps {
  $imageWidth: number
}

const ImageWrapper = styled.div<ImageWrapperProps>`
  height: 100%;
  width: ${(props) => props.$imageWidth}px;
  min-width: ${(props) => props.$imageWidth}px;
  overflow: hidden;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    width: 100%;
    min-width: 300px;
    padding-left: 8px;
    padding-right: 8px;
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`

const ButtonsWrapper = styled.div`
  bottom: 0px;
  background-color: white;
  border-top: 1px solid var(--tds-grey-500);
  width: 100vw;
  padding-top: 24px;
  padding-bottom: 16px;
  padding-right: 32px;
  padding-left: 32px;

  button {
    min-width: 200px;
    display: flex;
    justify-content: center;
  }
`

const InlineMenuWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
  touch-action: pan-y;
`

const TabsWrapper = styled.div`
  display: flex;
  width: 100%;

  align-items: center;
  border-bottom: 1px solid var(--tds-grey-300);
  overflow-x: hidden;
`

interface TabsButtonProps {
  $isSelected: boolean
}

const TabsButton = styled.div<TabsButtonProps>`
  padding-left: 16px;
  padding-right: 16px;
  width: fit-content;
  height: 100%;
  white-space: nowrap;
  color: ${(props) =>
    props.$isSelected
      ? 'var(--tds-inline-tabs-tab-color-selected)'
      : 'var(--tds-inline-tabs-tab-color)'};
  font-weight: bold;
  border-bottom: ${(props) =>
    props.$isSelected
      ? '2px solid var(--tds-blue-400)'
      : '2px solid transparent'};
  display: flex;
  align-items: center;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      border-bottom: ${(props) =>
        props.$isSelected
          ? '2px solid var(--tds-blue-400)'
          : '2px solid var(--tds-grey-400)'};
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    font-size: 13px;
  }
`

interface TabsScrollButtonProps {
  $disabled: boolean
}

const TabsScrollButton = styled.div<TabsScrollButtonProps>`
  min-width: 48px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--tds-grey-300);

  svg {
    height: 20px;
    color: var(--tds-blue-900);
    color: ${(props) =>
      props.$disabled ? 'var(--tds-grey-500)' : 'var(--tds-blue-900)'};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: ${(props) => (props.$disabled ? 'auto' : 'pointer')};
      background-color: var(--tds-grey-300);
    }
  }
`

const FullViewWrapper = styled.div`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    position: absolute;
    right: 12px;
    top: 0px;
    background-color: white;
    display: unset;
  }

  svg {
    height: 20px;
  }
`

export interface AllConfigurationsPageProps {
  handleNewClick: () => void
  handleRfqOrEysClick: () => void
  navigateToMenu: (
    id: string,
    menuPath: string,
    gvPageToJumpTo: GuidedViewPageType,
  ) => void
  handleFatalError: () => void
  handleSessionFailure: () => void
  handleCloseModalConsequenseOfChange: () => void
  handleConsequenceOfChange: ConsequenceOfChangeHandler
  highlightedId: string | null
}

export function AllConfigurationsPage({
  handleNewClick,
  handleRfqOrEysClick,
  navigateToMenu,
  handleFatalError,
  handleSessionFailure,
  handleCloseModalConsequenseOfChange,
  handleConsequenceOfChange,
  highlightedId,
}: AllConfigurationsPageProps): JSX.Element {
  const dispatch = useAppDispatch()
  const client = useClient()
  const t2 = useTextsV2()
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const { currentlyLoadingId, menuIdToOpenAtJumpToAllView } =
    useAppSelector(getMenuState)
  const menuTreeAllView =
    useAppSelector(getMenuState).menuResultAllView?.menuTree
  const selectedCabColourNode =
    useAppSelector(getMenuState).menuResultAllView?.selectedCabColorNode
  const selectedChassiColourNode =
    useAppSelector(getMenuState).menuResultAllView?.selectedChassiColorNode
  const isFactoryModel = useAppSelector(getTruckInfoState)?.isFactoryModel
  const [showFullImage, setShowFullImage] = useState<boolean>(
    isFactoryModel ? true : false,
  )
  const menuLevel2 = menuTreeAllView?.subMenus
    ? menuTreeAllView?.subMenus.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel3 = menuLevel2
    ? menuLevel2?.find((menu) => menu.isOpen === true)?.subMenus
    : null
  const menuLevel4 = menuLevel3
    ? menuLevel3.find((menu) => menu.isOpen === true)?.leafItems
    : null
  const selectedLeafItem = menuLevel4?.find((item) => item.isSelected === true)
  const [cabColorMenuLevel2Id, setCabColorMenuLevel2Id] = useState<
    string | null
  >(null)
  const [chassisColorMenuLevel2Id, setChassisColorMenuLevel2Id] = useState<
    string | null
  >(null)
  const waitingForResult = useRef(false)
  const scrollToItemRef = useRef<HTMLDivElement | null>(null)
  const [visibleHighlightedId, setVisibleHighlightedId] = useState<
    string | null
  >(null)
  const tabsWrapperRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [inlineMenuisOverflowing, setInlineMenuIsOverflowing] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const mainAreaRef = useRef<HTMLDivElement | null>(null)
  const size = useResizeObserver<Size2d>(rootRef, { width: 0, height: 0 })
  const [useDesktopLayout, setUseDesktopLayout] = useState<boolean>(true)
  const seriesFrame = useAppSelector(getSeriesFrame)
  const [pageReady, setPageReady] = useState<boolean>(false)
  const interiorMenuIdAllView =
    useAppSelector(getMenuState).menuResultAllView?.interiorMenuIdAllView
  const menuInititated = useRef(false)
  const [hidePageDescription, setHidePageDescription] = useState<boolean>(true)
  const urlCidParams = getUrlCidBevSemiliveParameters(UrlParamMode.ExcludeBev)
  const showFpcCodeOnHover = urlCidParams.has('fpc')
  const { rfqOrEysButtonHidden, rfqOrEysButtonText, rfqOrEysButtonEnabled } =
    useRfqEysButtonState()
  const superEngines = useAppSelector(getLatestConfigChangeResult)?.superEngines

  // Find "Cab color"- and "ChassisColor"-menu
  useEffect(() => {
    const cabColorMenuLevel3 = menuLevel3
      ? menuLevel3.find((menu) =>
          menu.leafItems?.some((s) => s.id === selectedCabColourNode?.id),
        )
      : null
    const cabColorMenuLevel2 =
      cabColorMenuLevel3 && menuLevel2
        ? menuLevel2.find((menu) =>
            menu.subMenus?.some((s) => s.id === cabColorMenuLevel3.id),
          )
        : null
    const chassisColorMenuLevel3 = menuLevel3
      ? menuLevel3.find((menu) =>
          menu.leafItems?.some((s) => s.id === selectedChassiColourNode?.id),
        )
      : null
    const chassisColorMenuLevel2 =
      chassisColorMenuLevel3 && menuLevel2
        ? menuLevel2.find((menu) =>
            menu.subMenus?.some((s) => s.id === chassisColorMenuLevel3.id),
          )
        : null

    if (cabColorMenuLevel2?.id) {
      setCabColorMenuLevel2Id(cabColorMenuLevel2?.id)
    }
    if (chassisColorMenuLevel2?.id) {
      setChassisColorMenuLevel2Id(chassisColorMenuLevel2.id)
    }
  }, [
    menuLevel2,
    menuLevel3,
    chassisColorMenuLevel2Id,
    cabColorMenuLevel2Id,
    selectedCabColourNode,
    selectedChassiColourNode,
  ])

  const updateImageView = useCallback(
    (submenu: GuidedViewMenuNode[] | null | undefined) => {
      if (
        submenu &&
        submenu?.find((menu) => menu.id === interiorMenuIdAllView)?.isOpen
      ) {
        if (seriesFrame.seriesId !== 'INTERIOR') {
          dispatch(setSeriesFrame({ seriesId: 'INTERIOR', frameId: 1 }))
        }
      } else if (seriesFrame.seriesId === 'INTERIOR') {
        dispatch(setSeriesFrame({ seriesId: 'EXTERIOR', frameId: 2 }))
      }
    },
    [dispatch, interiorMenuIdAllView, seriesFrame],
  )

  // Inititate menu
  useEffect(() => {
    if (menuInititated.current) {
      return
    }
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    if (menuTreeAllView && !menuIdToOpenAtJumpToAllView) {
      if (!pageReady) {
        updateImageView(menuTreeAllView.subMenus)
      }
      setPageReady(true)
      return
    }
    const asyncWrapper = async () => {
      menuInititated.current = true
      var menuId = menuIdToOpenAtJumpToAllView || null
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: menuId,
        showAllConfigMenu: true,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeAllView(newMenuState.success))
        dispatch(setMenuIdToOpenAtJumpToAllView(null))
        setPageReady(true)
        updateImageView(newMenuState.success.menuTree?.subMenus)
      }
    }
    asyncWrapper()
  }, [
    client,
    sessionInitData,
    dispatch,
    menuTreeAllView,
    interiorMenuIdAllView,
    updateImageView,
    pageReady,
    menuIdToOpenAtJumpToAllView,
  ])

  const handleMenuClick = (menuId: string, text: string) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: menuId,
        showAllConfigMenu: true,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeAllView(newMenuState.success))
        updateImageView(newMenuState.success.menuTree?.subMenus)

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: menuId,
            optionClickedText: text,
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
  }

  /*
  // TODO: Make a proper type or split verInfo to multiple objects
  const buildColorMenuInfo = (
    verInfo: null | (BuildInfoResponseWrapper & ScaniaSystemInfo),
    leafNodes: LeafMenuEntry[] | null,
  ): ColorMenuInfo | null => {
    if (!verInfo) {
      return null
    }

    // TODO - simplify color menu handling
    let isColorMenu = false
    const colorIds = verInfo?.tiledMenus.map((id) => id.split('_')[1]) || null
    const leafNodeIds = leafNodes?.map((node) => node.id.split('~')[0])
    if (leafNodeIds && colorIds) {
      const firstLeafId = leafNodeIds[0].split('~')[0]
      isColorMenu = colorIds.includes(firstLeafId)
    }
    const result: ColorMenuInfo = {
      colorIds,
      isColorMenu,
    }
    return result
  }
    */

  const handleReadmoreClick = useCallback(
    async (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      node: GuidedViewMenuLeafNode,
    ) => {
      e.stopPropagation()
      e.preventDefault()

      dispatch(setReadMoreNode({ id: node.id, title: node.shortText || null }))
      dispatch(openGvPanel())
      dispatch(setGvSidePanelContent(SidePanelContent.readMore))

      const trackingEvent: ScaniaAdobeTrackingClickBmReadMoreEvent = {
        event: ScaniaAdobeEventId.BmReadMoreClick,
      }
      pushAdobeEvent(trackingEvent)
    },
    [dispatch],
  )

  // Important to not include background-color here and set it with inline CSS on
  // the element attribute instead to avoid styled-components generating "over 200
  // classes", causing a visible lag in the GUI.
  // This can be solved with the .attrs function but that results in some super
  // ugly syntax.

  const blendColorToCss = (rgb: BlendColorRgb): string => {
    return `rgb(${rgb.R}, ${rgb.G}, ${rgb.B})`
  }

  const getColorVariantRgb = (id: string): BlendColorRgb | undefined => {
    return BlendColors[id]
  }

  const handleShowtruckClick = () => {
    const trackingEvent: ScaniaAdobeTrackingAllViewShowTruckButtonClick = {
      event: ScaniaAdobeEventId.AllViewShowTruckButtonClick,
      eventInfo: { viewOrHide: showFullImage ? 'hide' : 'view' },
    }
    pushAdobeEvent(trackingEvent)
    setShowFullImage(!showFullImage)
  }

  const handleItemSelection = useCallback(
    async (id: string, text: string) => {
      if (!sessionInitData) {
        throw new Error('Expected sessionInitData to be defined.')
      }
      if (!marketLanguage) {
        throw new Error('Expected marketLanguage to be defined.')
      }
      if (!client) {
        throw new Error('Expected client to be defined.')
      }
      if (waitingForResult.current) {
        console.log('Ignoring selection, waiting for previous result.')
        return
      }

      const isSmartDashCampaignItem = id.startsWith(SMART_DASH_FAMILY_ID + '~')
      const smartDashCampaignPreferredSeriesFrame: ImageSeriesFrame = {
        seriesId: 'INTERIOR',
        frameId: 1,
      }

      const trackingEvent: ScaniaAdobeTrackingConfigChangedEvent = {
        event: ScaniaAdobeEventId.ConfigChanged,
        eventInfo: {
          optionSelectedId: id,
          optionSelectedText: text,
        },
      }
      pushAdobeEvent(trackingEvent)

      const sessionId = sessionInitData.sessionId
      waitingForResult.current = true
      let tryChangeConfigResponse
      try {
        dispatch(setCurrentlyLoadingId(id))
        tryChangeConfigResponse = await client.tryChangeConfig({
          sessionId,
          input: id,
        })
      } catch {
        handleFatalError()
        dispatch(setCurrentlyLoadingId(null))
        waitingForResult.current = false
        return
      }
      if (tryChangeConfigResponse.error === SESSION_FAILURE) {
        handleSessionFailure()
        return
      }
      if (!tryChangeConfigResponse.success) {
        handleFatalError()
        return
      }
      const selectionResult = tryChangeConfigResponse.success
      dispatch(setCurrentlyLoadingId(null))

      const handleConfigChangeResult = (configChange: ConfigChangeResult) => {
        const updates = configChange.updates
        if (!updates) {
          throw new Error('Expected updates to be defined.')
        }
        dispatch(setUserSelectionTimestamp(new Date().getTime()))
      }

      const resultCode = selectionResult.resultCode
      if (resultCode === 'ACTIVATED') {
        if (!selectionResult.changeConfigResult) {
          throw new Error('Expected .changeConfigResult to be defined.')
        }

        // TODO: Eliminate one of setLatestConfigChangeResult and
        // handleUserSelection!
        dispatch(
          setLatestConfigChangeResult(selectionResult.changeConfigResult),
        )
        handleConfigChangeResult(selectionResult.changeConfigResult)

        const newMarketDenomination =
          selectionResult.changeConfigResult.auxCarrier.auxiliaries[
            'MarketDenomination'
          ].value
        dispatch(setMarketDenomination(newMarketDenomination || null))
        const unavailable = getUnavailableFrames(
          selectionResult.changeConfigResult,
        )
        dispatch(setUnavailableImageFrames(unavailable))
        if (isSmartDashCampaignItem) {
          dispatch(setSeriesFrame(smartDashCampaignPreferredSeriesFrame))
        }
      } else if (resultCode === 'RESOLVERS') {
        // TODO: THIS IS IMPORTANT AND WILL SIMPLIFY THE SCDS_BACKEND CODE AND
        // THIS GUI CODE SIGNIFICANTLY. Change the Consequence Of Change
        // handling to require a explicit rollback/cancel request instead of the
        // current solution where the rollback is implicit at the next
        // configuration update interaction unless the Consequence Of Change
        // suggestion is confirmed by the `bip` request.
        //
        // Change to EXPLICIT ROLLBACK as a dedicated request sent by the
        // client!
        if (!selectionResult.resolvers) {
          throw new Error('Expected selectionResult.resolvers to be defined.')
        }
        const cocData: ConsequenceOfChangeDialogData = {
          resolvers: selectionResult.resolvers,
          showCancelButton: true,
          clickedId: id,
        }
        const choice = await handleConsequenceOfChange(
          cocData,
          CocOpenedFromType.allView,
        )
        switch (choice) {
          case 'CANCEL':
            dispatch(closeAllModals())
            handleCloseModalConsequenseOfChange()
            break
          case 'OK':
            const ids = selectionResult.resolvers.triggerItems.map(
              (item: ConfigChangeMenuEntry) => item.id,
            )
            const changeConfigResult = await client.changeConfig({
              sessionId,
              input: ids.join(';'),
            })
            if (changeConfigResult.error === SESSION_FAILURE) {
              handleSessionFailure()
              return
            }
            if (!changeConfigResult.success) {
              console.error('Expected .success to be defined.')
              handleFatalError()
              return
            }

            // TODO: Eliminate one of setLatestConfigChangeResult and
            // handleUserSelection!
            dispatch(setLatestConfigChangeResult(changeConfigResult.success))
            dispatch(
              setMarketDenomination(
                changeConfigResult.success.auxCarrier.auxiliaries[
                  'MarketDenomination'
                ].value || null,
              ),
            )
            const unavailable = getUnavailableFrames(changeConfigResult.success)
            dispatch(setUnavailableImageFrames(unavailable))
            handleConfigChangeResult(changeConfigResult.success)

            dispatch(closeAllModals())
            handleCloseModalConsequenseOfChange()

            if (isSmartDashCampaignItem) {
              dispatch(setSeriesFrame(smartDashCampaignPreferredSeriesFrame))
            }
            break
          default:
            console.error('Unknown choice variant: ' + choice)
            handleFatalError()
            return
        }
      } else {
        handleFatalError()
        return
      }
      waitingForResult.current = false

      const parent = menuLevel3?.find((menu) => menu.isOpen === true)
      const parentId = parent?.id
      if (!parentId) {
        throw new Error('Expected parent id for ' + id)
      }
      const asyncWrapper = async () => {
        const newMenuState = await client.exploreMenu({
          sessionId: sessionInitData.sessionId,
          menuId: parentId,
          showAllConfigMenu: true,
        })
        if (newMenuState.success) {
          dispatch(setMenuTreeAllView(newMenuState.success))
        }
      }
      asyncWrapper()
    },
    [
      client,
      dispatch,
      handleConsequenceOfChange,
      handleFatalError,
      handleSessionFailure,
      handleCloseModalConsequenseOfChange,
      marketLanguage,
      sessionInitData,
      menuLevel3,
    ],
  )

  function getUnavailableFrames(
    rebopResult: ConfigChangeResult,
  ): ImageSeriesFrame[] {
    return (
      rebopResult.auxCarrier.auxiliaries['FrameFilter'].ignoredFrames
        ?.map<ImageSeriesFrame>((o) => ({
          seriesId: o.series,
          frameId: o.frame,
        }))
        .sort((a, b) => {
          const cmp = a.seriesId.localeCompare(b.seriesId)
          if (cmp !== 0) {
            return cmp
          }
          return a.frameId - b.frameId
        }) || []
    )
  }

  const handleOpenFullPageImageViewer = useCallback(() => {
    dispatch(setShowFullPageViewer(true))

    const trackingEvent: ScaniaAdobeTrackingClickFullImageEvent = {
      event: ScaniaAdobeEventId.FullImageClick,
    }
    pushAdobeEvent(trackingEvent)
  }, [dispatch])

  // For desktop, Scroll to leafItem on:
  //   1. Click on search result
  //   2. Click in ActiveChanges
  //   3. Click on Campaign link
  useLayoutEffect(() => {
    // It seems like Chrome cancels ongoing smooth scrolls when starting a new,
    // at least some times? That's why this scroll delay is here, the main menu
    // scroll must finish before starting this one.
    //
    // TODO: Consider using this event instead at a later time:
    // https://caniuse.com/mdn-api_element_scrollend_event
    //
    //Also delay the blink on selected item until scroll is finished.
    const scrollDelayMs = 700
    setTimeout(() => {
      if (scrollToItemRef.current) {
        scrollToItemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        setVisibleHighlightedId(highlightedId)
      }
    }, scrollDelayMs)
  }, [highlightedId, menuTreeAllView])

  const handleSelectedClick = () => {
    if (scrollToItemRef.current) {
      scrollToItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  const getImageWidth = () => {
    let imageWidth = 800
    const mainAreaHeight = mainAreaRef.current?.offsetHeight
    if (size.width < BreakpointWidthPx.Large) {
      imageWidth = size.width - 780
    }
    if (size.width < BreakpointWidthPx.Medium) {
      imageWidth = size.width - 400
    }
    if (mainAreaHeight && mainAreaHeight < imageWidth) {
      imageWidth = mainAreaHeight
    }
    return imageWidth
  }

  // Adobe user tracking.
  useEffect(() => {
    if (!marketLanguage) {
      return
    }
    dispatch(
      pushPageViewTrackingEvent({
        pageName: ScaniaAdobeTrackingPageName.GvAllOptions,
        marketLanguage,
      }),
    )
  }, [dispatch, marketLanguage])

  // Scrolls the inlineTabs menu left or right
  const handleScrollTabsClick = (direction: 'left' | 'right') => {
    if (tabsWrapperRef.current) {
      const scrollAmount = 100
      const newScrollLeft =
        direction === 'left'
          ? tabsWrapperRef.current.scrollLeft - scrollAmount
          : tabsWrapperRef.current.scrollLeft + scrollAmount

      tabsWrapperRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'instant',
      })
      updateScrollState()
    }
  }

  // Update scroll state of inline tabs
  useEffect(() => {
    updateScrollState()
  }, [size, tabsWrapperRef, menuTreeAllView])

  const updateScrollState = () => {
    if (tabsWrapperRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsWrapperRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(Math.round(scrollLeft) < scrollWidth - clientWidth - 1)
      setInlineMenuIsOverflowing(scrollWidth > clientWidth)
    }
  }

  useEffect(() => {
    const windowWidth = document.documentElement.clientWidth
    const windowHeight = document.documentElement.clientHeight
    if (
      windowWidth < BreakpointWidthPx.Small ||
      windowHeight < BreakpointHeightPx.Small
    ) {
      setUseDesktopLayout(false)
    } else {
      setUseDesktopLayout(true)
    }
  }, [size])

  const handleHidePageDescription = () => {
    setHidePageDescription(!hidePageDescription)
  }

  usePageUpdate(handleFatalError, handleSessionFailure)

  const bindDrag = useDrag(
    (drag) => {
      const swipeThreshold = 0.1
      if (tabsWrapperRef.current && drag.velocity[0] > swipeThreshold) {
        const newScrollLeft =
          tabsWrapperRef.current.scrollLeft - drag.movement[0]
        tabsWrapperRef.current.scrollTo({
          left: newScrollLeft,
          behavior: 'instant',
        })
        updateScrollState()
      }
    },
    {
      axis: 'x',
    },
  )

  return (
    <AllViewRoot
      data-name="GuidedViewAllRoot"
      ref={rootRef}
      $pageIsReady={pageReady}
    >
      {useDesktopLayout ? (
        <AllViewDesktop>
          <SubHeader
            handleNewClick={handleNewClick}
            handleSessionFailure={handleSessionFailure}
            navigateToMenu={navigateToMenu}
          />
          <HeaderSection>
            <HeaderWrapper>
              <Header>
                {t2(TextId.HEADER_GV_ALL_PRODUCT_SELECTIONS).toUpperCase()}
              </Header>
              <ChevronWrapper
                $hideText={hidePageDescription}
                onClick={handleHidePageDescription}
                data-test-element-type={
                  TestElementTypeId.AllViewDescriptionChevron
                }
              >
                <SvgChevronDown />
              </ChevronWrapper>
            </HeaderWrapper>
            {!hidePageDescription && (
              <DescriptionWrapper>
                <PageDescription>
                  {t2(TextId.DESCRIPTION_ALL_PRODUCT_SELECTIONS)}
                </PageDescription>
                <TdsButton
                  size="sm"
                  variant="secondary"
                  onClick={handleHidePageDescription}
                  text={t2(TextId.LABEL_ACTION_GOT_IT)}
                  style={{ minWidth: 'fit-content' }}
                  data-test-element-type={TestElementTypeId.AllViewGotItButton}
                />
              </DescriptionWrapper>
            )}
          </HeaderSection>
          <UpperMenuWrapper>
            <InlineMenuWrapper {...bindDrag()}>
              {inlineMenuisOverflowing && (
                <TabsScrollButton
                  onClick={() => handleScrollTabsClick('left')}
                  $disabled={!canScrollLeft}
                  data-test-element-type={
                    TestElementTypeId.AllViewTabsScrollLeftButton
                  }
                >
                  <SvgChevronLeft />
                </TabsScrollButton>
              )}
              <TabsWrapper ref={tabsWrapperRef}>
                {menuTreeAllView?.subMenus &&
                  menuTreeAllView.subMenus.map((menuItem, i) => {
                    return (
                      <TabsButton
                        onClick={() =>
                          handleMenuClick(menuItem.id, menuItem.shortText || '')
                        }
                        key={menuItem.id + 'guidedView'}
                        $isSelected={menuItem.isOpen}
                        id={'inlineMenu' + menuItem.id}
                        data-test-element-type={
                          TestElementTypeId.AllViewTabsButton
                        }
                        data-test-etel-variant-id={menuItem.id}
                      >
                        {i + 1 + '.  ' + (menuItem.shortText || menuItem.id)}
                        {showFpcCodeOnHover && (
                          <TdsTooltip
                            placement="bottom"
                            selector={'#inlineMenu' + menuItem.id}
                            text={menuItem.id}
                          />
                        )}
                      </TabsButton>
                    )
                  })}
              </TabsWrapper>
              {inlineMenuisOverflowing && (
                <TabsScrollButton
                  onClick={() => handleScrollTabsClick('right')}
                  $disabled={!canScrollRight}
                  data-test-element-type={
                    TestElementTypeId.AllViewTabsScrollRightButton
                  }
                >
                  <SvgChevronRight />
                </TabsScrollButton>
              )}
            </InlineMenuWrapper>
          </UpperMenuWrapper>
          <MainArea ref={mainAreaRef}>
            <SideMenu highlightedId={highlightedId} />
            <LeafContentRoot $showTruck={showFullImage}>
              <LeafContentScrollWrapper>
                <div>
                  <LeafHeader onClick={handleSelectedClick}>
                    <span style={{ fontWeight: 'bold' }}>
                      {t2(TextId.LABEL_CURRENT_SELECTION) + ': '}
                    </span>
                    {menuLevel2?.find((menu) => menu.isOpen === true)?.id ===
                    cabColorMenuLevel2Id
                      ? selectedCabColourNode?.shortText
                      : menuLevel2?.find((menu) => menu.isOpen === true)?.id ===
                        chassisColorMenuLevel2Id
                      ? selectedChassiColourNode?.shortText
                      : selectedLeafItem?.shortText}
                  </LeafHeader>
                  <LeafSection>
                    {menuLevel4 &&
                      menuLevel4.map((leafItem, i) =>
                        leafItem.isColorMenu ? (
                          <ColourItem
                            key={leafItem.id}
                            onClick={
                              leafItem.isSelected
                                ? () => {}
                                : () =>
                                    handleItemSelection(
                                      leafItem.id,
                                      leafItem.shortText || '',
                                    )
                            }
                            id={'colorItem' + i}
                            data-test-element-type={
                              TestElementTypeId.AllViewColourCard
                            }
                            data-test-etel-variant-id={leafItem.id}
                          >
                            <ColourCircle
                              $isSelected={leafItem.isSelected}
                              $isHighlighted={
                                leafItem.id === visibleHighlightedId
                              }
                              $color={blendColorToCss(
                                getColorVariantRgb(leafItem.id) || {
                                  R: 255,
                                  G: 0,
                                  B: 255,
                                },
                              )}
                              $isMetallic={leafItem.id.includes('02575')}
                              ref={
                                leafItem.isSelected
                                  ? scrollToItemRef
                                  : leafItem.id === highlightedId
                                  ? scrollToItemRef
                                  : null
                              }
                            >
                              {leafItem.isSelected && (
                                <TickWrapper>
                                  <SvgTick />
                                </TickWrapper>
                              )}
                              {currentlyLoadingId === leafItem.id && (
                                <SpinnerWrapper>
                                  <TdsSpinner size="sm" />
                                </SpinnerWrapper>
                              )}
                            </ColourCircle>
                            <ColourText>{leafItem.shortText}</ColourText>
                            {showFpcCodeOnHover && (
                              <TdsTooltip
                                placement="top"
                                selector={'#colorItem' + i}
                                text={leafItem.id}
                              />
                            )}
                          </ColourItem>
                        ) : (
                          <OptionCard
                            $isSelected={leafItem.isSelected}
                            $isHighlighted={
                              leafItem.id === visibleHighlightedId
                            }
                            key={leafItem.id}
                            onClick={
                              leafItem.isSelected
                                ? () => {}
                                : () =>
                                    handleItemSelection(
                                      leafItem.id,
                                      leafItem.shortText || '',
                                    )
                            }
                            ref={
                              leafItem.isSelected
                                ? scrollToItemRef
                                : leafItem.id === highlightedId
                                ? scrollToItemRef
                                : null
                            }
                            id={'nonColorItem' + i}
                            data-test-element-type={
                              TestElementTypeId.AllViewNonColourCard
                            }
                            data-test-etel-variant-id={leafItem.id}
                          >
                            <OptionCardHeader>
                              <OptionCardHeaderText>
                                {leafItem.shortText}
                              </OptionCardHeaderText>
                              <OptionCardStatusWrapper>
                                {leafItem.isSelected && <SvgTick />}
                                {currentlyLoadingId === leafItem.id && (
                                  <TdsSpinner size="sm" />
                                )}
                              </OptionCardStatusWrapper>
                            </OptionCardHeader>
                            <OptionCardContent>
                              <CardTextWrapper>
                                <div></div>
                                <CardText>
                                  {leafItem.readmoreText?.map(
                                    (textItem, index) => (
                                      <div key={index}>
                                        {textItem?.textParagraph ?? ''}
                                        {textItem?.textList ? (
                                          <div>
                                            • &nbsp; {textItem.textList}
                                          </div>
                                        ) : (
                                          ''
                                        )}
                                      </div>
                                    ),
                                  )}
                                </CardText>
                              </CardTextWrapper>
                              {leafItem.iconUrl && (
                                <CardImage>
                                  {leafItem.iconUrl && (
                                    <img
                                      src={leafItem.iconUrl || undefined}
                                      alt="truck"
                                    />
                                  )}
                                </CardImage>
                              )}
                            </OptionCardContent>
                            <ReadMoreAndSuperWrapper>
                              {leafItem.hasreadMore ? (
                                <OptionCardReadMore
                                  onClick={(e) =>
                                    handleReadmoreClick(e, leafItem)
                                  }
                                >
                                  <SvgInfo />
                                  <OptionCardReadMoreText>
                                    {t2(TextId.DESCRIPTION_VIEW_INFORMATION)}
                                  </OptionCardReadMoreText>
                                </OptionCardReadMore>
                              ) : (
                                <OptionCardReadMore />
                              )}
                              {superEngines &&
                                superEngines.some(
                                  (i) => i.id === leafItem.id,
                                ) && (
                                  <CardSuper>
                                    <SvgScaniaSuper />
                                  </CardSuper>
                                )}
                            </ReadMoreAndSuperWrapper>
                            {showFpcCodeOnHover && (
                              <TdsTooltip
                                placement="top"
                                selector={'#nonColorItem' + i}
                                text={leafItem.id}
                              />
                            )}
                          </OptionCard>
                        ),
                      )}
                  </LeafSection>
                </div>
              </LeafContentScrollWrapper>
            </LeafContentRoot>
            <ImageSection
              $showFullImage={showFullImage}
              $imageWidth={getImageWidth()}
            >
              <SlideInImageButton
                onClick={handleShowtruckClick}
                data-test-element-type={
                  TestElementTypeId.AllViewSlideInImageButton
                }
              >
                <DotAndArrowWrapper>
                  <GreyDot $showFullImage={showFullImage}>
                    <SvgArrowRight />
                  </GreyDot>
                </DotAndArrowWrapper>
                <TextSlideIn>
                  {showFullImage
                    ? t2(TextId.LABEL_ACTION_HIDE)
                    : t2(TextId.LABEL_ACTION_SHOW)}
                </TextSlideIn>
              </SlideInImageButton>
              <ImageWrapper $imageWidth={getImageWidth() - 32}>
                <MainImageArea
                  showImageControlPanel={true}
                  showFullViewIcon={true}
                  showSideArrows={false}
                  navigateToMenu={navigateToMenu}
                  imageControlPanelHidden={!showFullImage}
                />
              </ImageWrapper>
            </ImageSection>
          </MainArea>
          <AllViewFooter
            handleRfqOrEysClick={handleRfqOrEysClick}
            parentSize={size}
          />
        </AllViewDesktop>
      ) : (
        <AllViewMobile ref={scrollRef}>
          <MobileUpperSectionWrapper>
            <HeaderSection>
              <HeaderWrapper>
                <Header>
                  {t2(TextId.HEADER_GV_ALL_PRODUCT_SELECTIONS).toUpperCase()}
                </Header>
                <ChevronWrapper
                  $hideText={hidePageDescription}
                  onClick={handleHidePageDescription}
                  data-test-element-type={
                    TestElementTypeId.AllViewDescriptionChevron
                  }
                >
                  <SvgChevronDown />
                </ChevronWrapper>
              </HeaderWrapper>
              {!hidePageDescription && (
                <DescriptionWrapper>
                  <p>{t2(TextId.DESCRIPTION_ALL_PRODUCT_SELECTIONS)}</p>
                  <TdsButton
                    size="sm"
                    variant="secondary"
                    onClick={handleHidePageDescription}
                    text={t2(TextId.LABEL_ACTION_GOT_IT)}
                    style={{ minWidth: 'fit-content' }}
                  />
                </DescriptionWrapper>
              )}
            </HeaderSection>

            <ImageWrapper ref={imageRef} $imageWidth={getImageWidth()}>
              <MainImageArea
                showImageControlPanel={true}
                showFullViewIcon={true}
                showSideArrows={true}
                navigateToMenu={navigateToMenu}
              />
              <FullViewWrapper
                onClick={handleOpenFullPageImageViewer}
                data-test-element-type={TestElementTypeId.AllViewMaximizeImage}
              >
                <SvgMaximize />
              </FullViewWrapper>
              <MainImageControlPanelMobile showIcons={true} />
            </ImageWrapper>
          </MobileUpperSectionWrapper>

          <ConfiguratorMenuMobile
            highlightedId={highlightedId}
            parentSize={size}
            handleItemSelection={handleItemSelection}
          />
          {!rfqOrEysButtonHidden && (
            <ButtonsWrapper>
              <TdsButton
                variant="primary"
                text={rfqOrEysButtonText}
                size="md"
                onClick={handleRfqOrEysClick}
                fullbleed
                disabled={!rfqOrEysButtonEnabled}
                data-test-element-type={
                  TestElementTypeId.AllViewGetConsultationButton
                }
              >
                <TdsIcon slot="icon" size="16px" name="message" />
              </TdsButton>
            </ButtonsWrapper>
          )}
          <PortraitFooter displayAtScreenMaxWidth={BreakpointWidthPx.Small} />
        </AllViewMobile>
      )}
    </AllViewRoot>
  )
}
